import Breadcrumbs from "../../components/Common/Breadcrumb"
import React, { useEffect, useState } from "react"
import { Alert, Button, Card, CardBody, Col, Container, Row, Spinner, Table, UncontrolledTooltip } from "reactstrap"
import { withTranslation } from "react-i18next"
import { Link } from "react-router-dom"
import { del, get, handleApiError, post } from "../../helpers/api_helper"
import { getUserRoleFromToken } from "../../helpers/auth_token_helper"
import toastr from "toastr"

const UserManagement = props => {

  const [usersStore, setUsersStore] = useState([]);
  const [loading, setLoading] = useState(false);
  const role = getUserRoleFromToken();

  const fetchUserStore = async () => {
    setLoading(true);
    const responseUsers = await get('user-stores');
    if (200 === responseUsers.status) {
      setUsersStore(responseUsers.data['hydra:member']);
    }
    setLoading(false);
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString("fr-FR", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    }).replace(",", "");
  };

  const onManageAccount = async (id, action) => {
    try {
      const responseManage = await post(`user-stores/${id}/${action}`);
      if (200 === responseManage.status) {
        setUsersStore(usersStore.map(userStore => {
          if (userStore.id === id) {
            return {
              ...userStore,
              user: {
                ...userStore.user,
                active: 'enable' === action
              }
            };
          }
          return userStore;
        }));
        toastr.success('Vos modifications ont été enregistrées avec succès.');
      }
    } catch (error) {
      handleApiError(error.response.data);
    }

  }

  const onDelete = async (id) => {
    try {
      const response = await del('user-stores/' + id);

      if (204 === response.status) {
        setUsersStore(usersStore.filter(userStore => userStore.id !== id));
        toastr.success('Vos modifications ont été enregistrées avec succès.');
      }
    } catch (error) {
      handleApiError(error.response.data);
    }
  }

  useEffect(() => {
    fetchUserStore();
  }, []);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title={props.t("Gestion des utilisateurs et accès")}
                       breadcrumbItem={props.t("Gestion des utilisateurs et accès")} />

          <div className="mt-2">
            <Card className="shadow-sm">
              <CardBody>
                <p className="text-muted">
                  Cette interface vous permet de gérer efficacement les utilisateurs de votre Store.
                  Vous pouvez ajouter, modifier ou supprimer des comptes, attribuer des rôles et
                  surveiller l'activité des utilisateurs en toute simplicité.
                </p>

                <Row className="mt-3">
                  <Col md={6}>
                    <Alert color="info">
                      <i className="bi bi-person-plus-fill"></i> <strong>Ajout d'utilisateurs : </strong>
                      Créez de nouveaux comptes et attribuez-leur des rôles adaptés (Admin, Manager, Vendeur).
                    </Alert>
                  </Col>
                  <Col md={6}>
                    <Alert color="warning">
                      <i className="bi bi-shield-lock-fill"></i> <strong>Gestion des accès : </strong>
                      Définissez les permissions de chaque utilisateur et ajustez leurs droits en fonction de leur rôle.
                    </Alert>
                  </Col>
                </Row>

                <Row className="mt-3">
                  <Col md={6}>
                    <Alert color="success">
                      <i className="bi bi-eye-fill"></i> <strong>Suivi des activités : </strong>
                      Consultez l'historique des connexions et surveillez les actions effectuées par chaque utilisateur.
                    </Alert>
                  </Col>
                  <Col md={6}>
                    <Alert color="danger">
                      <i className="bi bi-person-x-fill"></i> <strong>Désactivation et suppression : </strong>
                      Bloquez temporairement un compte ou supprimez un utilisateur définitivement si nécessaire.
                    </Alert>
                  </Col>
                </Row>
              </CardBody>
            </Card>
          </div>

          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <h3 className="text-primary font-size-15">
                <i className="bx bxs-user"></i> Liste des utilisateurs
              </h3>
              {
                'admin' === role
                && (
                  <Link to="/users/add" className="btn btn-outline-primary">
                    <i className="mdi mdi-plus-circle-outline me-1"></i> Ajouter un utilisateur
                  </Link>
                )
              }
            </div>
            <Table striped bordered hover responsive className="shadow-sm">
              <thead className="table-light">
              <tr>
                <th>Nom</th>
                <th>Email</th>
                <th>Rôle</th>
                <th>Statut</th>
                <th>Dernière connexion</th>
                <th>Actions</th>
              </tr>
              </thead>
              <tbody>
              {usersStore && usersStore.length > 0 ? (
                usersStore.map((userStore) => (
                  <tr key={userStore.user.id}>
                    <td>{userStore.user.fullName}</td>
                    <td>{userStore.user.email}</td>
                    <td>{userStore.role.name}</td>
                    <td>{userStore.user.active ? '✅ actif': '❌ inactif'}</td>
                    <td>{userStore.user.lastLogin ? formatDate(userStore.user.lastLogin) : '-'}</td>
                    <td className="text-center">
                      <div className="d-flex align-items-center gap-4">
                        {
                          true === userStore.user.active
                          ?
                            (
                              <Link to={'#'} onClick={() => onManageAccount(userStore.id, 'disable')}>
                                <i className="mdi mdi-account-cancel font-size-18 text-danger" id="disableTooltip" />
                                <UncontrolledTooltip placement="top" target="disableTooltip">
                                  Désactiver ce compte
                                </UncontrolledTooltip>
                              </Link>
                            )
                          :
                            (
                              <Link to={'#'} onClick={() => onManageAccount(userStore.id, 'enable')}>
                                <i className="mdi mdi-account-check font-size-18 text-info" id="enableTooltip" />
                                <UncontrolledTooltip placement="top" target="enableTooltip" key={userStore.user.id}>
                                  Activer ce compte
                                </UncontrolledTooltip>
                              </Link>
                            )
                        }
                        <Link to={'#'} onClick={() => onDelete(userStore.id)}>
                          <i className="mdi mdi-account-remove font-size-18 text-danger" id="deletetooltip" />
                          <UncontrolledTooltip placement="top" target="deletetooltip" key={userStore.user.id}>
                            Supprimer
                          </UncontrolledTooltip>
                        </Link>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="text-center text-muted">Aucun utilisateur..</td>
                </tr>
              )}
              </tbody>
            </Table>
          </div>
        </Container>
      </div>
    </React.Fragment>
  );
}

export default withTranslation()(UserManagement);
